// src/components/BookNow.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import {
  Label,
  TextInput,
  Button,
  Select,
  Checkbox,
  Card,
} from "flowbite-react";
import { HiMail, HiArrowCircleRight } from "react-icons/hi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { GraduationCap, Globe, UserCheck, Award, Stethoscope } from "lucide-react";
// Assets

import unithink from "../assets/UniThink.webp";

// Swiper
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// Slides
import sas_slide from "../assets/booknow_sas.webp";
import nurs_gmy_slide from "../assets/booknow_Nursing_Germany.webp";
import counseling_slide from "../assets/booknow_counselling.webp";
import japan_slide from "../assets/booknow_japan.webp";
import dubai_slide from "../assets/booknow_dubai.webp";
import mba_bengaluru_slide from "../assets/mba_bangalore_hero_new.png";

function BookNow() {
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  // NOTE: Booking form kept the same; serviceType initialized to "" to avoid uncontrolled warnings
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phonenumber: "",
    country: "",
    serviceType: "",
    agreeTerms: false,
    contactPreference: false,
    receiveUpdates: false,
  });

  const [successMessage, setSuccessMessage] = useState(null);
  const [statistics, setStatistics] = useState({
    universitiesTied: 0,
    coursesAvailable: 0,
    satisfactionRate: 0,
    visaAcceptance: 0,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setStatistics((prevStats) => ({
        ...prevStats,
        universitiesTied:
          prevStats.universitiesTied < 1500
            ? prevStats.universitiesTied + 10
            : 1500,
        coursesAvailable:
          prevStats.coursesAvailable < 25 ? prevStats.coursesAvailable + 1 : 25,
        satisfactionRate:
          prevStats.satisfactionRate < 2500
            ? prevStats.satisfactionRate + 30
            : 2500,
        visaAcceptance:
          prevStats.visaAcceptance < 98 ? prevStats.visaAcceptance + 1 : 98,
      }));
    }, 10);

    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await axios.post(
        "https://unithink-backend.vercel.app/api/consultation/",
        formData
      );
      // If you have an email service, keep the call here
      // await sendEmail(formData);

      setLoading(false);
      setSuccessMessage("Form submitted successfully!");
      toast.success("Form submitted successfully!");
      setFormData({
        name: "",
        email: "",
        country: "",
        phonenumber: "",
        serviceType: "",
        agreeTerms: false,
        contactPreference: false,
        receiveUpdates: false,
      });
      setErrorMessage(null);
    } catch (error) {
      console.error("Error sending data:", error);
      setLoading(false);
      setErrorMessage("Failed to submit form. Please try again later.");
      toast.error("Failed to submit form. Please try again later.");
    }
  };

  const handleChange = (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.id]: value });
  };

  const highlights = [
    {
      icon: <UserCheck className="w-6 h-6 text-slate-600" />,
      title: "Career Mentoring",
      desc: "1:1 mentor calls, streams & skills mapping, college shortlists, action plans.",
    },
    {
      icon: <Award className="w-6 h-6 text-slate-600" />,
      title: "SAS Certification",
      desc: "Authorised partner-led prep. Hands-on labs. Job-aligned analytics tracks.",
    },
    {
      icon: <Globe className="w-6 h-6 text-slate-600" />,
      title: "Study Abroad",
      desc: "Transparent, university-first counselling. Timelines, SOPs, scholarships.",
    },
    {
      icon: <GraduationCap className="w-6 h-6 text-slate-600" />,
      title: "Study in India",
      desc: "Clarity on exams, colleges & careers. Domain mentors and alumni panels.",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Toasts */}
      <ToastContainer position="top-right" />

      {/* ====== HERO ====== */}
      <section className="relative overflow-hidden bg-gradient-to-r  from-slate-400 via-red-450 to-black text-white">
        {/* Decorative student image (desktop) */}
        {/*<img
          src={student1}
          alt="Decor"
          className="pointer-events-none select-none absolute -top-10 -left-10 md:left-1/2 transform -translate-x-1/2 max-w-xs md:max-w-md hidden lg:block opacity-20"
        />*/}

        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          {/* Branding + CTA */}
          <div className="space-y-4 text-center lg:text-left">
            {/* Top row → ReThink + UniThink */}
            <div className="flex items-center gap-4 justify-center lg:justify-start">

              <img src={unithink} alt="UniThink" className="h-10 md:h-12" />
            </div>

            {/* Bottom row → Boundless 
  <div className="flex justify-center lg:justify-start">
    <img src={boundless} alt="Boundless" className="h-10 md:h-12" />
  </div>*/}





            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight">
              Your Passport to Global Education & Career Success
            </h1>
            <p className="text-white/90 text-lg max-w-xl mx-auto lg:mx-0">
              From counselling to skilling, and university selection to visas —
              we guide you end-to-end on your journey.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center lg:justify-start">

              <Button size="lg" className="bg-white text-indigo-700 font-bold hover:bg-yellow-400 hover:text-white "
                onClick={() => {
                  const el = document.getElementById("bookform");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}>
                Book Now!
              </Button>

              <Button
                size="lg"
                color="light"
                className="bg-white/10 hover:bg-white/20 border-white/30"
                onClick={() => {
                  const el = document.getElementById("services");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                Explore Services
              </Button>
            </div>

            {/* MBBS Promo Callout */}
            <div className="mt-10 flex justify-center lg:justify-start">
              <Link
                to="/mbbs-admissions-india-abroad"
                className="relative group flex items-center justify-center sm:justify-start gap-4 bg-white/10 hover:bg-white/20 border border-[#FF0000]/50 px-6 py-4 rounded-xl backdrop-blur-md transition-all shadow-[0_0_15px_rgba(255,0,0,0.3)] hover:shadow-[0_0_25px_rgba(255,0,0,0.5)] w-full sm:w-auto"
              >
                {/* Highlight Badge */}
                <div className="absolute -top-3 -right-2 sm:-right-3 bg-[#FF0000] text-white text-[10px] sm:text-xs font-black px-3 py-1 rounded-full shadow-lg animate-bounce">
                  NEW
                </div>

                <div className="bg-white text-[#FF0000] p-2.5 rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                  <Stethoscope size={24} />
                </div>
                <div className="text-left">
                  <span className="block text-red-600 font-extrabold text-lg leading-tight mb-0.5">MBBS Admissions 2026</span>
                  <span className="block text-white/90 text-sm font-medium group-hover:translate-x-1 transition-transform">India & Abroad &rarr;</span>
                </div>
              </Link>
            </div>
          </div>

          {/* Booking form (unchanged UI & behavior) */}
          <div id="bookform" className="z-10 w-full">
            <div className="z-10 p-5 bg-white/95 text-slate-900 border rounded-2xl shadow-2xl">
              <p className="z-10 text-lg text-center md:text-left">
                Book Free Counselling & Guidance
              </p>

              {/* ====== BOOKING FORM START (kept exactly as provided) ====== */}
              <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                <div>
                  <Label value="Name*" />
                  <TextInput
                    type="text"
                    placeholder="Enter your name"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="rounded-md shadow-sm border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  />
                </div>
                <div>
                  <Label value="Email*" />
                  <TextInput
                    type="email"
                    placeholder="Enter your email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    rightIcon={HiMail}
                    className="rounded-md shadow-sm border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  />
                </div>
                <div>
                  <Label value="Phone*" />
                  <TextInput
                    type="number"
                    placeholder="Enter your number"
                    id="phonenumber"
                    value={formData.phonenumber}
                    onChange={handleChange}
                    className="rounded-md shadow-sm border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  />
                </div>
                <div>
                  <Label value="Country*" />
                  <TextInput
                    type="text"
                    placeholder="Enter your country"
                    id="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="rounded-md shadow-sm border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  />
                </div>
                <div>
                  <Label value="Service Type*" />
                  <Select
                    id="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    required
                    className="rounded-md shadow-sm border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  >
                    <option value="">Select Service Type</option>
                    <option value="Education & Career Counselling">
                      Education & Career Counselling
                    </option>
                    <option value="Find Your University & Course ">
                      Find Your University & Course{" "}
                    </option>
                    <option value="Skilling Academy ">Skilling Academy </option>
                    <option value="Education & Business Counsultancy">
                      Education & Business Counsultancy
                    </option>
                  </Select>
                </div>
                <label htmlFor="receiveUpdates" className="text-sm">
                  UniThink will not share your details with others without your permission:
                </label>
                <div className="flex items-center">
                  <Checkbox
                    id="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <label htmlFor="agreeTerms" className="text-sm">
                    I agree to Unithink Terms{" "}
                    <a href="/terms" className="text-blue-500">
                      UniThink Terms
                    </a>{" "}
                    and{" "}
                    <a href="/privacypolicy" className="text-blue-500">
                      privacy policy
                    </a>
                  </label>
                </div>
                <div className="flex items-center">
                  <Checkbox
                    id="contactPreference"
                    checked={formData.contactPreference}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <label htmlFor="contactPreference" className="text-sm">
                    Please contact me by phone, email or SMS to assist with my enquiry
                  </label>
                </div>
                <div className="flex items-center">
                  <Checkbox
                    id="receiveUpdates"
                    checked={formData.receiveUpdates}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <label htmlFor="receiveUpdates" className="text-sm">
                    I would like to receive updates and offers from UniThink
                  </label>
                </div>
                <Button className="bg-blue-800" type="submit" disabled={loading}>
                  {loading ? "Booking..." : "Book Now!"}
                </Button>
              </form>
              {/* ====== BOOKING FORM END ====== */}
            </div>
          </div>
        </div>
      </section>

      {/* ====== STATS ====== */}
      <section className="py-12 md:py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 px-6">
          {[
            { value: `${statistics.coursesAvailable}+`, label: "Years of Career Counselling" },
            { value: `${statistics.universitiesTied}+`, label: "Partner Universities" },
            { value: `${statistics.satisfactionRate}+`, label: "Students & Executives Trained" },
            { value: `${statistics.visaAcceptance}%`, label: "Visa Acceptance" },
          ].map((stat, i) => (
            <div
              key={i}
              className="p-6 bg-white/70 backdrop-blur-xl rounded-2xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition"
            >
              <h2 className="text-3xl font-extrabold text-blue-800 text-center">{stat.value}</h2>
              <p className="text-slate-600 mt-2 text-center">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ====== SLIDESHOW ====== */}
      <section className="max-w-7xl mx-auto px-6 pb-6">
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={20}
          slidesPerView={1}
          navigation
          pagination={{ clickable: true }}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          loop={true}
          className="rounded-2xl shadow-2xl"
        >
          {[
            { img: mba_bengaluru_slide, link: "/mba-in-bengaluru", alt: "Study MBA in Bengaluru", desc: "Ignite Your Corporate Career" },
            { img: sas_slide, link: "/sas", alt: "SAS Certification", desc: "Authorized partner-led training" },
            { img: nurs_gmy_slide, link: "/germany/nursing", alt: "Nursing in Germany", desc: "Pathway to global healthcare" },
            { img: counseling_slide, link: "/counseling", alt: "Career Counseling", desc: "1:1 mentoring & college mapping" },
            { img: japan_slide, link: "/japan", alt: "Study in Japan", desc: "Experience world-class education" },
            { img: dubai_slide, link: "/study-work-dubai", alt: "Study in Dubai", desc: "Dynamic global opportunities" },
          ].map((slide, i) => (
            <SwiperSlide key={i} onClick={() => navigate(slide.link)}>
              <div className="relative group cursor-pointer w-full h-[250px] md:h-[400px] lg:h-[450px] overflow-hidden rounded-2xl">
                <img
                  src={slide.img}
                  alt={slide.alt}
                  className="w-full h-full object-cover rounded-2xl transition-transform duration-700 group-hover:scale-105"
                />

                {/* Permanent Gradient & Title Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent rounded-2xl flex flex-col justify-end p-6 md:p-10 pointer-events-none">
                  <div className="transform transition-transform duration-500 translate-y-2 group-hover:translate-y-0">
                    <h3 className="text-white text-3xl md:text-5xl font-extrabold shadow-sm tracking-tight mb-2">
                      {slide.alt}
                    </h3>
                    <p className="text-blue-200 text-lg md:text-xl font-medium opacity-90 mb-2">
                      {slide.desc}
                    </p>
                    <div className="inline-flex items-center text-white font-bold tracking-wide mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      Explore Now <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>



      {/* ====== SERVICES ====== */}
      <section id="services" className="py-16  bg-gradient-to-b from-slate-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 text-center mb-10">
            What We Offer
          </h2>
          <section className="py-12 bg-white rounded-2xl">
            <div className="max-w-6xl mx-auto px-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {highlights.map((item, index) => (
                  <div
                    key={index}
                    className="rounded-2xl border border-slate-200 p-6 text-center sm:text-left shadow-sm hover:shadow-md transition"
                  >
                    <div className="flex items-center justify-center sm:justify-start gap-2 mb-3">
                      {item.icon}
                      <h3 className="font-semibold text-2xl">{item.title}</h3>
                    </div>
                    <p className="text-slate-600 text-lg">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </section>

      {/* ====== BOTTOM CTA ====== */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto text-center px-6">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900">
            Ready to start your journey?
          </h3>
          <p className="text-slate-600 mt-2">
            Book a free counselling session and get personalized guidance.
          </p>
          <div className="mt-6 flex justify-center ">

            <Button size="lg" className="bg-blue-800 hover:bg-indigo-700 text-white font-semibold"
              onClick={() => {
                const el = document.getElementById("bookform");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}>
              Book Now
            </Button>

          </div>
        </div>
      </section>
    </div>
  );
}

export default BookNow;
